// apiClient.js
import { Capacitor } from '@capacitor/core';
import logger from './logger'; // Assuming logger.js is also in utils/

export const API_BASE_URL = Capacitor.isNativePlatform() ? 'https://api.langjee.com' : (process.env.REACT_APP_API_URL || 'http://localhost:5000');
const DEFAULT_TIMEOUT = 60000;

// This is our dependency injection point.
// It will hold a reference to the `getToken` function from FirebaseContext.
let tokenProvider = async () => {
    logger.warn('apiClient - Auth tokenProvider has not been set yet.');
    return null;
};

const apiClient = {
    // Call this from your FirebaseContext to hook everything up.
    setAuthTokenProvider: (provider) => {
        logger.info('apiClient - Auth tokenProvider has been set.');
        tokenProvider = provider;
    },

    // The main fetch function now uses the injected tokenProvider.
    async fetchWithAuth(url, options = {}) {
        const fullUrl = `${API_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;

        try {
            const token = await tokenProvider(); // Use the provider
            if (!token) {
                throw new Error('Authentication required: No token available.');
            }

            const headers = { ...options.headers, 'Authorization': `Bearer ${token}` };
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), options.timeout || DEFAULT_TIMEOUT);

            const response = await fetch(fullUrl, { ...options, headers, signal: controller.signal });
            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`HTTP error ${response.status}: ${errorBody}`);
            }

            // We will no longer handle 401 retries here.
            // The getToken function in FirebaseContext is now responsible for providing a valid token.
            // If a 401 still happens, it's a legitimate error (e.g., permissions).
            return response;

        } catch (error) {
            logger.error(`apiClient - Error in fetchWithAuth for ${fullUrl}:`, error);
            throw error;
        }
    },

    // --- All the helper methods (get, post, put, delete) can now be much simpler ---

    async get(url, options = {}) {
        const response = await this.fetchWithAuth(url, { ...options, method: 'GET' });
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        // The caller is responsible for parsing JSON.
        return response;
    },

    async post(url, body, options = {}) {
        const response = await this.fetchWithAuth(url, {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...options.headers },
            body: JSON.stringify(body),
        });

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            // Always return the parsed JSON for consistency.
            return response.json();
        }
        
        // For non-JSON responses, we can return null or an empty object 
        // if the callers are expecting an object. Let's return the response
        // and let the caller decide. But most post calls expect a json.
        return response;
    },

    async put(url, body, options = {}) {
        const response = await this.fetchWithAuth(url, {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...options.headers },
            body: JSON.stringify(body),
        });

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }
        
        return response;
    },

    async delete(url, options = {}) {
        const { data, ...restOptions } = options;
        const fetchOptions = { ...restOptions, method: 'DELETE' };

        // Support sending a body with DELETE requests (e.g., for clearVocabulary)
        if (data) {
            fetchOptions.body = JSON.stringify(data);
            fetchOptions.headers = { 'Content-Type': 'application/json', ...fetchOptions.headers };
        }

        const response = await this.fetchWithAuth(url, fetchOptions);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        return response;
    },
};

export default apiClient;
